// src/data/portalConfig.ts
// ------------------------
// Repository for the `portal_config` table — a simple key→JSONB-value store for
// admin-editable settings (standard costs, next-steps templates, onboarding
// email templates, system alert email, …). Typed via getTypedClient().

import type { Database, Json } from "../types/database.generated";
import { logger } from "../utils/logger";
import { isValidEmail, normalizeEmail } from "../utils/sanitize";
import { getTypedClient } from "../utils/supabase";
import { invalidEmailMessage } from "./emailFields";

type PortalConfigInsert = Database["public"]["Tables"]["portal_config"]["Insert"];

// The JSONB value for a single config key, or null when the key is absent /
// there's no client / on error. Callers apply their own shape + defaults.
export async function getConfigValue(key: string): Promise<unknown> {
  const c = getTypedClient();
  if (!c) return null;
  // Never throw — the documented contract is "null on error". A thrown exception
  // (e.g. an unexpected client shape) is caught here too, so callers that don't
  // .catch() (like useStandardCosts) can't raise an unhandled rejection.
  try {
    const { data, error } = await c
      .from("portal_config")
      .select("value")
      .eq("key", key)
      .maybeSingle();
    if (error) {
      logger.warn("getConfigValue failed:", error.message);
      return null;
    }
    return data?.value ?? null;
  } catch (e) {
    logger.warn("getConfigValue error:", e);
    return null;
  }
}

// Several keys at once (admin screens that edit a group of templates). Returns
// the raw { key, value } rows (caller maps key→value), or [] / null.
export async function getConfigValues(
  keys: string[]
): Promise<Array<{ key: string; value: unknown }> | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("portal_config").select("key, value").in("key", keys);
  if (error) {
    logger.warn("getConfigValues failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as Array<{ key: string; value: unknown }>;
}

// The admin-editable settings that hold an email address (email-validation-plan §6B).
// Plain-string values …
const EMAIL_STRING_KEYS = new Set([
  "feedback_notify_email",
  "school_locker_alert_email",
  "school_locker_order_email",
]);
// … and { email, … } object values (a blank email is allowed here — e.g. the School
// Locker BCC switched OFF — so only a non-blank address is normalised/validated).
const EMAIL_OBJECT_KEYS = new Set(["system_alert_email", "school_locker_bcc"]);

// Normalise (trim + lowercase) the email inside a known email setting and refuse an
// invalid one. Every other key passes through unchanged.
export function cleanConfigEmail(
  key: string,
  value: unknown
): { value: unknown; error: { message: string } | null } {
  if (EMAIL_STRING_KEYS.has(key) && typeof value === "string") {
    const email = normalizeEmail(value);
    if (!isValidEmail(email)) return { value, error: { message: invalidEmailMessage(value) } };
    return { value: email, error: null };
  }
  if (EMAIL_OBJECT_KEYS.has(key) && value && typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (typeof obj.email !== "string" || obj.email.trim() === "") return { value, error: null };
    const email = normalizeEmail(obj.email);
    if (!isValidEmail(email)) return { value, error: { message: invalidEmailMessage(obj.email) } };
    return { value: { ...obj, email }, error: null };
  }
  return { value, error: null };
}

// Upsert a single key's value (admin saves). Conflict target is the key.
export async function upsertConfig(
  key: string,
  rawValue: unknown
): Promise<{ error: { message: string } | null }> {
  const { value, error: emailError } = cleanConfigEmail(key, rawValue);
  if (emailError) return { error: emailError };
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("portal_config")
    .upsert({ key, value: value as Json } as PortalConfigInsert, { onConflict: "key" });
  if (error) logger.warn("upsertConfig failed:", error.message);
  return { error };
}

// Delete a key (admin "reset to defaults").
export async function deleteConfig(key: string): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("portal_config").delete().eq("key", key);
  if (error) logger.warn("deleteConfig failed:", error.message);
  return { error };
}
